import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { learningModuleService, LearningModule, SubscribedModule } from '../../services/learningModuleService';
import { learningProgressService, LearningProgress } from '../../services/learningProgressService';
import './Learn.css';

interface LearnProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

// Using LearningModule interface from the service
// Adding display properties for backward compatibility with existing UI
interface DisplayModule extends LearningModule {
  id: string; // Required for UI compatibility
  level: string; // Required for UI display
  progress: number; // Required for progress display
  icon: string; // Required for UI display
  folder: string; // Required for navigation
  isSubscribed?: boolean; // Track subscription status
  subscription_id?: string; // Subscription ID if subscribed
  subscription_status?: 'subscribed' | 'unsubscribed' | 'completed'; // Subscription status
}

const Learn: React.FC<LearnProps> = ({
  user,
  onClose,
  onToggleSidebar
}) => {
  const navigate = useNavigate();
  const [subscribedModules, setSubscribedModules] = useState<DisplayModule[]>([]);
  const [availableModules, setAvailableModules] = useState<DisplayModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'subscribed' | 'all'>('subscribed');

  useEffect(() => {
    const loadModules = async () => {
      try {
        setLoading(true);
        
        // Load subscriptions, all modules, and user progress in parallel
        const [subscriptions, allModules, userProgressData] = await Promise.all([
          learningModuleService.getMySubscriptions({ status: 'subscribed' }),
          learningModuleService.getAllLearningModules(),
          learningProgressService.getUserLearningProgress({ progress_type: 'module' })
        ]);
        
        console.log('🔍 Learn::loadModules::Subscriptions:', subscriptions);
        console.log('🔍 Learn::loadModules::All modules:', allModules);
        console.log('🔍 Learn::loadModules::User progress:', userProgressData);
        
        // Create a map of module progress by module_id for quick lookup
        const progressMap = new Map<string, LearningProgress>();
        userProgressData.progress.forEach(progress => {
          progressMap.set(progress.module_id, progress);
        });
        
        // Create a set of subscribed module IDs for quick lookup
        const subscribedModuleIds = new Set(subscriptions.map(sub => sub.module_id));
        
        // Transform subscribed modules to display format with actual progress
        const displaySubscribedModules: DisplayModule[] = subscriptions.map(sub => {
          const moduleProgress = progressMap.get(sub.module_id);
          const actualProgress = moduleProgress ? moduleProgress.completion_percentage : 0;
          
          return {
            module_id: sub.module_id,
            url_alias: sub.url_alias,
            title: sub.title,
            description: sub.description,
            status: sub.status,
            category: sub.category,
            created_at: sub.created_at,
            updated_at: sub.updated_at,
            id: sub.module_id,
            level: 'Intermediate',
            progress: actualProgress,
            icon: getCategoryIcon(sub.category),
            folder: sub.url_alias,
            isSubscribed: true,
            subscription_id: sub.subscription_id,
            subscription_status: sub.subscription_status
          };
        });
        
        // Filter out subscribed modules from available modules
        const displayAvailableModules: DisplayModule[] = allModules
          .filter(module => !subscribedModuleIds.has(module.module_id))
          .map(module => ({
            ...module,
            id: module.module_id,
            level: 'Intermediate',
            progress: 0,
            icon: getCategoryIcon(module.category),
            folder: module.url_alias,
            isSubscribed: false
          }));
        
        setSubscribedModules(displaySubscribedModules);
        setAvailableModules(displayAvailableModules);
        setError(null);
      } catch (err) {
        console.error('Error loading modules:', err);
        setError('Failed to load learning modules');
        setSubscribedModules([]);
        setAvailableModules([]);
      } finally {
        setLoading(false);
      }
    };

    loadModules();
  }, []);

  // Helper function to get icon based on category
  const getCategoryIcon = (category: string): string => {
    switch (category.toLowerCase()) {
      case 'language':
        return '🌍';
      case 'psychology':
        return '🧠';
      case 'science':
        return '🔬';
      case 'technology':
        return '💻';
      case 'mathematics':
        return '📐';
      default:
        return '📚';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="learn-status-badge completed">✓ Completed</span>;
      case 'in_progress':
        return <span className="learn-status-badge in-progress">📚 In Progress</span>;
      case 'available':
        return <span className="learn-status-badge available">🚀 Available</span>;
      default:
        return null;
    }
  };

  const getLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'beginner':
        return 'beginner';
      case 'elementary':
        return 'elementary';
      case 'intermediate':
        return 'intermediate';
      case 'advanced':
        return 'advanced';
      default:
        return 'default';
    }
  };

  const handleModuleClick = (moduleId: string) => {
    navigate(`/learn/${moduleId}`);
  };

  // Handle subscription toggle
  const handleSubscriptionToggle = async (moduleId: string, isSubscribed: boolean) => {
    try {
      if (isSubscribed) {
        await learningModuleService.unsubscribeFromModule(moduleId);
      } else {
        await learningModuleService.subscribeToModule(moduleId);
      }
      
      // Reload modules and progress to reflect changes
      const [subscriptions, allModules, userProgressData] = await Promise.all([
        learningModuleService.getMySubscriptions({ status: 'subscribed' }),
        learningModuleService.getAllLearningModules(),
        learningProgressService.getUserLearningProgress({ progress_type: 'module' })
      ]);
      
      // Create a map of module progress by module_id for quick lookup
      const progressMap = new Map<string, LearningProgress>();
      userProgressData.progress.forEach(progress => {
        progressMap.set(progress.module_id, progress);
      });
      
      const subscribedModuleIds = new Set(subscriptions.map(sub => sub.module_id));
      
      const displaySubscribedModules: DisplayModule[] = subscriptions.map(sub => {
        const moduleProgress = progressMap.get(sub.module_id);
        const actualProgress = moduleProgress ? moduleProgress.completion_percentage : 0;
        
        return {
          module_id: sub.module_id,
          url_alias: sub.url_alias,
          title: sub.title,
          description: sub.description,
          status: sub.status,
          category: sub.category,
          created_at: sub.created_at,
          updated_at: sub.updated_at,
          id: sub.module_id,
          level: 'Intermediate',
          progress: actualProgress,
          icon: getCategoryIcon(sub.category),
          folder: sub.url_alias,
          isSubscribed: true,
          subscription_id: sub.subscription_id,
          subscription_status: sub.subscription_status
        };
      });
      
      const displayAvailableModules: DisplayModule[] = allModules
        .filter(module => !subscribedModuleIds.has(module.module_id))
        .map(module => ({
          ...module,
          id: module.module_id,
          level: 'Intermediate',
          progress: 0,
          icon: getCategoryIcon(module.category),
          folder: module.url_alias,
          isSubscribed: false
        }));
      
      setSubscribedModules(displaySubscribedModules);
      setAvailableModules(displayAvailableModules);
    } catch (err) {
      console.error('Error toggling subscription:', err);
    }
  };

  return (
    <div className="learn-container">
      <div className="learn-header">
        <div className="learn-header-left">
          <button 
            className="learn-menu-btn"
            onClick={onToggleSidebar}
            title="Toggle sidebar"
          >
            ☰
          </button>
          <h1 className="learn-title">/learn</h1>
        </div>
        <div className="learn-header-right">
          <button 
            className={`learn-view-toggle-btn ${viewMode === 'all' ? 'active' : ''}`}
            onClick={() => setViewMode(viewMode === 'subscribed' ? 'all' : 'subscribed')}
            title={viewMode === 'subscribed' ? 'View all modules' : 'View subscribed modules'}
          >
            {viewMode === 'subscribed' ? '📚 All Modules' : '📝 My Modules'}
          </button>

        </div>
      </div>

      <div className="learn-content">
        <div className="learn-intro">
          <p className="learn-description">
            Explore our curated learning modules to enhance your life skills and knowledge.
            Track your progress and continue where you left off.
          </p>
        </div>

        {loading && (
          <div className="learn-loading">
            <p>Loading modules...</p>
          </div>
        )}

        {error && (
          <div className="learn-error">
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            {viewMode === 'subscribed' ? (
              /* Subscribed Modules View */
              subscribedModules.length > 0 ? (
                <div className="learn-section">
                  <div className="learn-section-header">
                    <h2 className="learn-section-title">/my-modules</h2>
                    <span className="learn-section-count">{subscribedModules.length}</span>
                  </div>
                  <div className="learn-modules-grid">
                    {subscribedModules.map((module) => (
                      <div 
                        key={module.id} 
                        className={`learn-module-card ${module.subscription_status}`}
                        onClick={() => handleModuleClick(module.id)}
                      >
                        <div className="learn-module-header">
                          <div className="learn-module-icon">{module.icon}</div>
                          <div className="learn-module-info">
                            <h3 className="learn-module-title">{module.title}</h3>
                            <span className={`learn-level-badge ${getLevelColor(module.level)}`}>
                              {module.level}
                            </span>
                          </div>
                          {module.subscription_status === 'completed' && (
                            <span className="learn-status-badge completed">✓ Completed</span>
                          )}
                        </div>
                        
                        <p className="learn-module-description">{module.description}</p>
                        
                        {module.progress !== undefined && (
                          <div className="learn-progress-section">
                            <div className="learn-progress-header">
                              <span className="learn-progress-label">Progress</span>
                              <span className="learn-progress-percentage">{module.progress}%</span>
                            </div>
                            <div className="learn-progress-bar">
                              <div 
                                className="learn-progress-fill" 
                                style={{ width: `${module.progress}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                        
                        <div className="learn-module-actions">
                          <button 
                            className="learn-action-btn continue"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleModuleClick(module.id);
                            }}
                          >
                            {module.subscription_status === 'completed' ? '📝 Review' : '▶️ Continue'}
                          </button>
                          <button 
                            className="learn-action-btn unsubscribe"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSubscriptionToggle(module.id, true);
                            }}
                          >
                            Unsubscribe
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="learn-empty-state">
                  <p>You haven't subscribed to any modules yet.</p>
                  <button 
                    className="learn-action-btn start"
                    onClick={() => setViewMode('all')}
                  >
                    📚 Browse All Modules
                  </button>
                </div>
              )
            ) : (
              /* All Modules View */
              <div className="learn-section">
                <div className="learn-section-header">
                  <h2 className="learn-section-title">/all-modules</h2>
                  <span className="learn-section-count">{subscribedModules.length + availableModules.length}</span>
                </div>
                <div className="learn-modules-grid">
                  {/* Show subscribed modules first */}
                  {subscribedModules.map((module) => (
                    <div 
                      key={module.id} 
                      className={`learn-module-card ${module.subscription_status} subscribed-indicator`}
                      onClick={() => handleModuleClick(module.id)}
                    >
                      <div className="learn-module-header">
                        <div className="learn-module-icon">{module.icon}</div>
                        <div className="learn-module-info">
                          <h3 className="learn-module-title">{module.title}</h3>
                          <span className={`learn-level-badge ${getLevelColor(module.level)}`}>
                            {module.level}
                          </span>
                        </div>
                        <span className="learn-subscribed-badge">✓ Subscribed</span>
                      </div>
                      
                      <p className="learn-module-description">{module.description}</p>
                      
                      {module.progress !== undefined && (
                        <div className="learn-progress-section">
                          <div className="learn-progress-header">
                            <span className="learn-progress-label">Progress</span>
                            <span className="learn-progress-percentage">{module.progress}%</span>
                          </div>
                          <div className="learn-progress-bar">
                            <div 
                              className="learn-progress-fill" 
                              style={{ width: `${module.progress}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                      
                      <div className="learn-module-actions">
                        <button 
                          className="learn-action-btn continue"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleModuleClick(module.id);
                          }}
                        >
                          {module.subscription_status === 'completed' ? '📝 Review' : '▶️ Continue'}
                        </button>
                        <button 
                          className="learn-action-btn unsubscribe"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSubscriptionToggle(module.id, true);
                          }}
                        >
                          Unsubscribe
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {/* Show available modules */}
                  {availableModules.map((module) => (
                    <div 
                      key={module.id} 
                      className={`learn-module-card ${module.status}`}
                      onClick={() => handleModuleClick(module.id)}
                    >
                      <div className="learn-module-header">
                        <div className="learn-module-icon">{module.icon}</div>
                        <div className="learn-module-info">
                          <h3 className="learn-module-title">{module.title}</h3>
                          <span className={`learn-level-badge ${getLevelColor(module.level)}`}>
                            {module.level}
                          </span>
                        </div>
                        {getStatusBadge(module.status)}
                      </div>
                      
                      <p className="learn-module-description">{module.description}</p>
                      
                      <div className="learn-module-actions">
                        <button 
                          className="learn-action-btn start"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleModuleClick(module.id);
                          }}
                        >
                          🚀 Start Learning
                        </button>
                        <button 
                          className="learn-action-btn subscribe"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSubscriptionToggle(module.id, false);
                          }}
                        >
                          Subscribe
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Learn;
